import { useEffect } from "react";
import { VehicleProps } from "../utils/vehiclesInfo";
import { imagePrefetchService } from "../services/imagePrefetchService";
import { useUrlMapCache } from "./useUrlMapCache";
import { UrlMapData } from "../interface/components";

export function usePrefetchImages(vehicle: VehicleProps) {
  const urlMapData = useUrlMapCache() as UrlMapData;

  useEffect(() => {
    if (!vehicle || !vehicle.models || !urlMapData) return;

    const carModel = vehicle.name?.toLowerCase();
    const carData = carModel && urlMapData[carModel];
    if (!carData) return;

    // Coleta URLs de imagens externas, internas e carrosséis
    let carrosselUrls: string[] = [
      ...(carData.external || []),
      ...(carData.internal || []),
    ];
    Object.values(carData.models).forEach((modelData) => {
      Object.values(modelData.versions).forEach((versionData) => {
        Object.values(versionData.colors).forEach((colorData) => {
          if (colorData.carrossel) {
            carrosselUrls.push(colorData.carrossel);
          }
        });
      });
    });
    // Remove duplicatas
    carrosselUrls = Array.from(new Set(carrosselUrls));

    // Prioriza imagens do primeiro modelo
    const currentModel = vehicle.models[0]?.name?.toLowerCase();
    const priorityUrls = currentModel
      ? Array.from(
          new Set([
            ...carrosselUrls.filter((url) => url.includes(currentModel)),
            ...carrosselUrls,
          ])
        ).slice(0, 5)
      : carrosselUrls.slice(0, 5);
    const remainingUrls = carrosselUrls.slice(5);

    // Prefetch prioritário imediato
    imagePrefetchService.addToPrefetchQueue(priorityUrls);

    // Prefetch das restantes usando requestIdleCallback (ou fallback para setTimeout)
    const idleCallback =
      window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 3000));
    const idleId = idleCallback(() => {
      imagePrefetchService.addToPrefetchQueue(remainingUrls);
    });
    return () => {
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, [vehicle, urlMapData]);

  return {
    prefetchedCount: imagePrefetchService.getPrefetchedUrls().size,
    isComplete: imagePrefetchService.isComplete(),
  };
}
